import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Keyboard, Search } from 'lucide-react';
import { DashboardLayout, PageHeader } from '../common/page_component/index.js';
import { useAuth } from '../../context/AuthContext.js';
import { getShortcutGroups } from '../../utils/appShortcuts.js';
import { flattenNavDestinations, formatNavGoChord, NAV_GO_PREFIX } from '../../utils/sidebarMenu.js';
import { filterInputClass, listScopeTabSmClass } from './uiClasses.js';

const KeyboardShortcutsPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const prefix = NAV_GO_PREFIX.toUpperCase();
  const [query, setQuery] = useState('');
  const [activeGroup, setActiveGroup] = useState('all');

  const groups = useMemo(() => {
    const prefixLabel = `${prefix} then`;
    const actionGroups = getShortcutGroups({
      user,
      pathname: '/keyboard-shortcuts',
      fullGuide: true,
    }).map((group) => {
      if (group.id !== 'general') return group;
      return {
        ...group,
        items: group.items.filter((item) => !String(item.keys).startsWith(prefixLabel)),
      };
    });

    const goItems = [
      {
        keys: formatNavGoChord(' '),
        action: 'Search pages',
        description: 'Jump list of every screen you can open. Type to filter, then Enter.',
      },
      ...flattenNavDestinations(user)
        .filter((row) => row.primary && row.goKey)
        .map((row) => ({
          keys: formatNavGoChord(row.goKey),
          action: row.label || t(row.labelKey),
          description: 'Opens this module from the sidebar.',
          path: row.path,
        })),
    ];

    return [
      { id: 'goto', title: 'Go to a page', items: goItems },
      ...actionGroups.filter((group) => group.items.length > 0),
    ];
  }, [user, t, prefix]);

  const categories = useMemo(
    () => [{ id: 'all', title: 'All' }, ...groups.map((group) => ({ id: group.id, title: group.title }))],
    [groups],
  );

  const visibleGroups = useMemo(() => {
    const q = query.trim().toLowerCase();
    return groups
      .filter((group) => activeGroup === 'all' || group.id === activeGroup)
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => {
          if (!q) return true;
          return `${item.action} ${item.description || ''} ${item.keys}`.toLowerCase().includes(q);
        }),
      }))
      .filter((group) => group.items.length > 0);
  }, [groups, activeGroup, query]);

  const totalCount = groups.reduce((sum, group) => sum + group.items.length, 0);

  return (
    <DashboardLayout>
      <div className="lg:hidden bg-white border border-gray-100 rounded-xl p-5 text-sm text-gray-600">
        Keyboard shortcuts are for desktop, Windows, macOS, and Linux. Open this page on a computer to see keys for your role.
      </div>

      <div className="hidden lg:block w-full min-w-0">
        <PageHeader
          title={
            <span className="inline-flex items-center gap-2">
              <Keyboard className="w-5 h-5 sm:w-6 sm:h-6 text-secondary shrink-0" />
              Keyboard shortcuts
            </span>
          }
          subtitle={`${totalCount} shortcuts for ${user?.role || 'your role'}. Filtered by what you can actually use.`}
        />

        <div className="grid grid-cols-3 gap-3 mb-5">
          <HowToCard step="1" title={`Hold nothing — press ${prefix}`}>
            Starts a jump. You have about one second for the next key.
          </HowToCard>
          <HowToCard step="2" title="Press the second key">
            A letter opens a page. Space searches every page you can access.
          </HowToCard>
          <HowToCard step="3" title="Skip when typing">
            Shortcuts ignore keys while a search box or form field is focused.
          </HowToCard>
        </div>

        <div className="flex flex-col xl:flex-row xl:items-center gap-3 mb-5">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Find a shortcut"
              className={`${filterInputClass} pl-9`}
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {categories.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => setActiveGroup(cat.id)}
                className={listScopeTabSmClass(activeGroup === cat.id)}
              >
                {cat.title}
              </button>
            ))}
          </div>
        </div>

        {visibleGroups.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 px-4 py-10 text-center text-sm text-gray-500">
            No shortcuts match that search.
          </div>
        ) : (
          <div className={`grid grid-cols-1 gap-4 ${visibleGroups.length > 1 ? 'xl:grid-cols-2' : ''}`}>
            {visibleGroups.map((group) => (
              <section key={group.id} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden min-w-0">
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 bg-gray-50">
                  <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500">{group.title}</h2>
                  <span className="text-[11px] text-gray-400">{group.items.length}</span>
                </div>
                <ul>
                  {group.items.map((item) => (
                    <li key={`${group.id}-${item.keys}-${item.action}`} className="border-b border-gray-50 last:border-0">
                      {item.path ? (
                        <button
                          type="button"
                          onClick={() => navigate(item.path)}
                          className="w-full text-left px-4 py-2.5 hover:bg-gray-50 transition-colors"
                        >
                          <ShortcutRow item={item} />
                        </button>
                      ) : (
                        <div className="px-4 py-2.5">
                          <ShortcutRow item={item} />
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

function HowToCard({ step, title, children }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-3.5">
      <p className="text-[11px] font-semibold text-gray-400 mb-1">Step {step}</p>
      <p className="text-sm font-semibold text-gray-800">{title}</p>
      <p className="text-xs text-gray-500 mt-1 leading-relaxed">{children}</p>
    </div>
  );
}

function ShortcutRow({ item }) {
  return (
    <div className="flex items-center justify-between gap-4 min-w-0">
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-800 truncate">{item.action}</p>
        {item.description ? (
          <p className="text-xs text-gray-500 mt-0.5 leading-snug line-clamp-2">{item.description}</p>
        ) : null}
      </div>
      <KeySequence keys={item.keys} />
    </div>
  );
}

function KeyCap({ children }) {
  return (
    <span className="inline-flex min-w-[1.6rem] h-7 items-center justify-center px-1.5 rounded-md border border-gray-200 bg-white text-[11px] font-semibold text-gray-800 shadow-[inset_0_-1px_0_#e5e7eb,0_1px_0_#d1d5db]">
      {children}
    </span>
  );
}

function KeySequence({ keys }) {
  const raw = String(keys || '').trim();

  const wrap = (nodes) => (
    <span className="inline-flex items-center gap-1 shrink-0">{nodes}</span>
  );

  const joinWith = (parts, sep) =>
    parts.map((part, i) => (
      <React.Fragment key={`${part}-${i}`}>
        {i > 0 ? <span className="text-[10px] text-gray-400 px-0.5">{sep}</span> : null}
        <KeyCap>{part.trim()}</KeyCap>
      </React.Fragment>
    ));

  if (/\sthen\s/i.test(raw)) {
    return wrap(joinWith(raw.split(/\s+then\s+/i), 'then'));
  }
  if (raw.includes(' or ')) {
    return wrap(joinWith(raw.split(/\s+or\s+/i), 'or'));
  }
  if (raw.includes(' / ')) {
    return wrap(joinWith(raw.split(/\s+\/\s+/i), '/'));
  }
  if (raw.includes('–')) {
    return wrap(joinWith(raw.split('–'), '–'));
  }
  if (raw.includes('+')) {
    return wrap(raw.split('+').map((part, i) => <KeyCap key={`${part}-${i}`}>{part.trim()}</KeyCap>));
  }
  return wrap(<KeyCap>{raw}</KeyCap>);
}

export default KeyboardShortcutsPage;
